import { createFormHookContexts, useStore, type AnyFieldApi } from '@tanstack/react-form';

/**
 * `Object.is`, because shallow equality fails on a `File` (zero own keys); an explicit subscription for
 * the React Compiler. The `any` casts: Derived/ReadonlyStore miss `useStore`'s AnyAtom across versions.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function useFieldValue<T>(field: { store: any; state: { value: T } }): T {
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	return useStore(field.store, (state: any) => state.value, Object.is as any);
}

export function hasFieldError(meta: AnyFieldApi['state']['meta']): boolean {
	if (meta.isValid) return false;
	const hasSubmitErrors = (meta.errorMap as Record<string, unknown[]> | undefined)?.onSubmit?.length;
	return meta.isTouched || !!hasSubmitErrors;
}

/** For a field that renders its own message slot, like `AmountInput`, to point `aria-describedby` at it. */
export function fieldErrorMessage(fieldState: AnyFieldApi['state']): string | undefined {
	const { meta } = fieldState;
	if (!hasFieldError(meta)) return undefined;
	return meta.errors.map((error: Error) => error.message).join(', ') || undefined;
}

// TODO: `field.state`, not `field`, for tanstack form under the React Compiler: https://github.com/TanStack/form/issues/1379
export function FieldInfo(props: { fieldState: AnyFieldApi['state'] }) {
	const message = fieldErrorMessage(props.fieldState);
	if (message == null) return null;
	return (
		<p data-slot="form-message" className="text-destructive m-1 text-sm">
			{message}
		</p>
	);
}

export const { fieldContext, formContext, useFieldContext, useFormContext } = createFormHookContexts();

/** An empty value cannot pass: an enum, a number, or a pipe action rejecting the empty case. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function hasRequiredConstraint(schema: any): boolean {
	if (schema?.type === 'enum' || schema?.type === 'picklist' || schema?.type === 'number') return true;
	// A literal must match exactly, so no empty value passes it.
	if (schema?.type === 'literal') return true;

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const actions: any[] | undefined = schema?.pipe;
	if (!Array.isArray(actions)) return false;

	return actions.some((action) => {
		if (action?.type === 'min_length' || action?.type === 'min_size') return Number(action.requirement) >= 1;
		if (action?.type === 'min_value') return Number(action.requirement) > 0;
		// The exclusive bound, as in `.positive()`: at or above 0 excludes empty.
		if (action?.type === 'gt_value') return Number(action.requirement) >= 0;
		// email(), url() and the like cannot be empty.
		return action?.type === 'email' || action?.type === 'url' || action?.type === 'iso_date';
	});
}

/** Through valibot's `.type`, `.pipe`, `.wrapped` and `.entries`; `undefined` when the path cannot be resolved. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function isOptionalInSchema(schema: any, fieldPath: string): boolean | undefined {
	const parts = fieldPath.replace(/\[(\d+)\]/g, '.$1').split('.');
	let current = schema;

	for (const part of parts) {
		if (!current?.type) return undefined;
		while (current?.type === 'optional' || current?.type === 'nullish' || current?.type === 'nullable') {
			current = current.wrapped;
		}

		if ((current?.type === 'object' || current?.type === 'loose_object') && current.entries) {
			current = current.entries[part];
		} else if (current?.type === 'array' && /^\d+$/.test(part)) {
			current = current.item;
		} else if (current?.type === 'record') {
			// A checked record (translatableText requiring content) makes its values required.
			if (Array.isArray(current.pipe) && current.pipe.length > 1) return false;
			current = current.value;
		} else {
			return undefined;
		}
	}

	if (!current?.type) return undefined;
	// `optional`/`nullish` wrappers are the explicit "may be absent" marker.
	if (current.type === 'optional' || current.type === 'nullish') return true;

	// A union written to allow clearing — `File | '' | undefined` — is optional, while `File | undefined`
	// is NOT: there, undefined means "no change", not "no value".
	if (current.type === 'union' && Array.isArray(current.options)) {
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		const options: any[] = current.options;
		const hasUndefined = options.some((o) => o?.type === 'undefined');
		const hasLiteralEmpty = options.some((o) => o?.type === 'literal' && o?.literal === '');
		if (hasUndefined) return hasLiteralEmpty;
	}

	return !hasRequiredConstraint(current);
}

export function useIsFieldOptional(fieldName: string, explicitOptional?: boolean): boolean {
	const field = useFieldContext();
	if (explicitOptional !== undefined) return explicitOptional;

	const validators = field.form.options.validators;
	const formSchema = validators?.onSubmit ?? validators?.onChange ?? validators?.onBlur;
	if (formSchema) {
		const result = isOptionalInSchema(formSchema, fieldName);
		if (result !== undefined) return result;
	}

	return !(
		field.options.validators?.onSubmit ??
		field.options.validators?.onChange ??
		field.options.validators?.onBlur
	);
}
